import { useState } from 'react';
import { Pcomment } from 'types/pcomment';
import { UserInfo } from 'types/user_info';
import timeHelper from 'utils/timeHelper';
import axios from 'utils/axiosInstance';
import CommentForm from './CommentForm';

interface CommentItemProps {
    comment: Pcomment;
    noteId: number;
    onCommentCreated: (comment: Pcomment) => void;
    isLoggedIn: boolean;
    userInfo: UserInfo | null;
}

const CommentItem = ({ comment, noteId, onCommentCreated, isLoggedIn, userInfo }: CommentItemProps) => {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const isAuthor = userInfo && comment.user_id === userInfo.id;
    const isAdmin = userInfo?.roleinfo?.isAllAdminGroup;
    const canDelete = comment.can_delete ?? (isAuthor || isAdmin);

    const handleDelete = async () => {
        if (!confirm('정말로 이 댓글을 삭제하시겠습니까?')) {
            return;
        }

        setIsDeleting(true);
        try {
            const response = await axios.delete(`/api/v1/notes/${noteId}/pcomments/${comment.id}`);
            if (response.data.success) {
                alert('댓글이 삭제되었습니다.');
                onCommentCreated(comment); // 상위 컴포넌트 리프레시를 위해 호출
            } else {
                alert(response.data.message || '삭제에 실패했습니다.');
            }
        } catch (error: any) {
            console.error('댓글 삭제 실패:', error);
            alert(error.response?.data?.message || '삭제 중 오류가 발생했습니다.');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className={`py-4 border-b border-gray-100 ${comment.depth > 0 ? 'ml-8 bg-gray-50/50 pl-4 border-l-2 border-gray-200' : ''}`}>
            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-gray-800">{comment.nick || comment.name}</span>
                    {comment.hidden && <span className="badge badge-ghost badge-sm text-[10px]">🔒 비공개</span>}
                    <span className="text-xs text-gray-400">{timeHelper.timestr(comment.created_at, 3)}</span>
                </div>
                <div className="flex gap-3">
                    {!showReplyForm && isLoggedIn && comment.depth === 0 && (
                        <button
                            onClick={() => setShowReplyForm(true)}
                            className="text-xs text-blue-500 hover:text-blue-700 cursor-pointer"
                        >
                            답글달기
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1 disabled:opacity-50 cursor-pointer"
                            title="삭제"
                        >
                            {isDeleting ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="w-4 h-4"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
                                    />
                                </svg>
                            )}
                        </button>
                    )}
                </div>
            </div>

            <div className={`text-sm whitespace-pre-wrap break-words mb-3 ${comment.content === '비밀 댓글입니다.' ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                {comment.content}
            </div>

            {showReplyForm && (
                <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
                    <CommentForm
                        noteId={noteId}
                        parentId={comment.id}
                        isLoggedIn={isLoggedIn}
                        onCommentCreated={(newComment) => {
                            onCommentCreated(newComment);
                            setShowReplyForm(false);
                        }}
                        onCancel={() => setShowReplyForm(false)}
                    />
                </div>
            )}
        </div>
    );
};

export default CommentItem;
